import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Drawer, Select, TextField, toast } from "@outpave/ds";
import { toastMutationError } from "utils/toastMutationError";
import CardProgramStatus from "enums/CardProgramStatus";
import {
  PlatformIntegration,
  SavePlatformIntegrationInput,
} from "hooks/usePlatformIntegrations";
import { Stack } from "components/layout/reusableLayouts";

interface IntegrationDetailsDrawerProps {
  open: boolean;
  integration: PlatformIntegration;
  onClose: () => void;
  onSave: (input: SavePlatformIntegrationInput) => Promise<unknown>;
}

/** An integration's name and whether it is in use. */
const IntegrationDetailsDrawer: React.FC<IntegrationDetailsDrawerProps> = ({
  open,
  integration,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [status, setStatus] = useState<CardProgramStatus>(
    CardProgramStatus.ACTIVE,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(integration.name);
    setStatus(integration.status ?? CardProgramStatus.ACTIVE);
  }, [open, integration]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave({ id: integration.id, name: name.trim(), status });
      toast.success(t("IntegrationSavedToast"));
      onClose();
    } catch (error: unknown) {
      toastMutationError(error, t("IntegrationSaveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Edit details")}
      loading={submitting}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={() => void handleSubmit()}
            disabled={submitting || !name.trim()}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <Stack gap="var(--space-4)">
        <TextField
          label={t("Name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
          helperText={t("IntegrationNameHelp")}
          required
          fullWidth
        />
        <Select
          label={t("Status")}
          size="md"
          value={status}
          onChange={(id) => setStatus(id as CardProgramStatus)}
          clearable={false}
          fullWidth
          helperText={
            integration.isDefault
              ? t("DefaultIntegrationStaysActive")
              : undefined
          }
          options={[
            { id: CardProgramStatus.ACTIVE, label: t("Active") },
            {
              id: CardProgramStatus.INACTIVE,
              label: t("Inactive"),
              disabled: integration.isDefault,
            },
          ]}
        />
      </Stack>
    </Drawer>
  );
};

export default IntegrationDetailsDrawer;
